import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { typography } from '../theme/typography';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useTypography } from '../hooks/useTypography';
import { TemplateGroup } from '../hooks/useCampaignTemplateGroups';
import { useThemedStyles } from '../hooks/useThemedStyles';

export interface CampaignPerformanceTableProps {
  groups: TemplateGroup[];
  isGrouping: boolean;
  onViewTemplate: (group: TemplateGroup) => void;
  previewLimit?: number;
}

const DEFAULT_PREVIEW_LIMIT = 5;

/** Campaigns grouped by the template they ran against — one row per
 * template group, mirroring UsersTeamCard.tsx's search + preview-slice
 * pattern. While the hook is still resolving the template join (isGrouping),
 * the single ungrouped placeholder bucket renders as one row with an inline
 * spinner rather than blocking the card's first paint. */
export const CampaignPerformanceTable: React.FC<CampaignPerformanceTableProps> = ({ groups, isGrouping, onViewTemplate, previewLimit = DEFAULT_PREVIEW_LIMIT }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const typo = useTypography();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q
      ? groups.filter((g) => (g.templateName ?? '').toLowerCase().includes(q))
      : groups;
    return base.slice(0, previewLimit);
  }, [groups, query, previewLimit]);

  return (
    <View style={styles.container}>
      <View style={[styles.searchBox, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name="search-outline" size={14} color={colors.textLight} />
        <TextInput
          style={[styles.searchInput, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]}
          placeholder={t('dashboardAnalytics.campaigns.search')}
          placeholderTextColor={colors.textLight}
          value={query}
          onChangeText={setQuery}
        />
      </View>

      <View style={styles.list}>
        {filtered.map((group, i) => {
          const successRate = group.totals.sent > 0 ? group.totals.delivered / group.totals.sent : 0;
          const displayName = group.templateName ?? t('dashboardAnalytics.campaigns.templateOther');
          const count = group.campaigns.length;

          return (
            <TouchableOpacity
              key={group.templateName ?? `placeholder-${i}`}
              style={styles.row}
              onPress={() => onViewTemplate(group)}
              activeOpacity={0.7}
              disabled={isGrouping}
            >
              <View style={[styles.rowTop, { flexDirection: rtl.rowDirection }]}>
                <View style={[styles.nameGroup, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="document-text-outline" size={14} color={colors.primary} />
                  <Text style={[styles.campaignName, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.medium }]} numberOfLines={1}>
                    {isGrouping ? t('dashboardAnalytics.campaigns.grouping') : displayName}
                  </Text>
                </View>
                {isGrouping ? (
                  <ActivityIndicator size="small" color={colors.textLight} />
                ) : (
                  <View style={styles.countBadge}>
                    <Text style={[styles.countBadgeText, { fontFamily: typo.fontFamily.semibold }]}>
                      {count} {t(count === 1 ? 'dashboardAnalytics.campaigns.groupCountOne' : 'dashboardAnalytics.campaigns.groupCount')}
                    </Text>
                  </View>
                )}
              </View>

              <View style={[styles.metricsRow, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.metric, { fontFamily: typo.fontFamily.primary }]}>{t('dashboardAnalytics.campaigns.sent')}: {group.totals.sent}</Text>
                <Text style={[styles.metric, { fontFamily: typo.fontFamily.primary }]}>{t('dashboardAnalytics.campaigns.delivered')}: {group.totals.delivered}</Text>
                <Text style={[styles.metric, { fontFamily: typo.fontFamily.primary }]}>{t('dashboardAnalytics.campaigns.read')}: {group.totals.read}</Text>
              </View>
              <View style={styles.barTrack}>
                <View style={[styles.barFill, { width: `${Math.round(successRate * 100)}%` }]} />
              </View>

              {!isGrouping && (
                <View style={[styles.viewPerformance, { flexDirection: rtl.rowDirection }]}>
                  <Text style={[styles.viewPerformanceText, { fontFamily: typo.fontFamily.semibold }]}>
                    {t('dashboardAnalytics.campaigns.viewPerformance')}
                  </Text>
                  <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={12} color={colors.primary} />
                </View>
              )}
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    gap: spacing.sm,
  },
  searchBox: {
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.sm,
  },
  searchInput: {
    flex: 1,
    height: 32,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  list: {
    gap: spacing.sm,
  },
  row: {
    paddingVertical: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: 4,
  },
  rowTop: {
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
  },
  nameGroup: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: 4,
  },
  campaignName: {
    flex: 1,
    minWidth: 0,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  countBadge: {
    paddingHorizontal: spacing.xs,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: colors.background.secondary,
  },
  countBadgeText: {
    fontSize: 10,
    color: colors.textLight,
  },
  metricsRow: {
    gap: spacing.sm,
  },
  metric: {
    fontSize: 10,
    color: colors.textLight,
  },
  barTrack: {
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.background.secondary,
    overflow: 'hidden',
  },
  barFill: {
    height: '100%',
    borderRadius: 2,
    backgroundColor: colors.primary,
  },
  viewPerformance: {
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 2,
    marginTop: 2,
  },
  viewPerformanceText: {
    fontSize: 10,
    color: colors.primary,
  },
});
